import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { Check, Trash2, UserX, X } from "lucide-react";
import { CreatorEarnings } from "@/components/admin/CreatorEarnings";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface CreatorApplication {
  id: string;
  username: string;
  application_date: string;
  application_status: string;
}

interface User {
  id: string;
  username: string;
  created_at: string;
  creator_status: string;
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<CreatorApplication[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkSuperuser();
    fetchApplications();
    fetchUsers();
  }, []);

  const checkSuperuser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/login");
        return;
      }

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('is_superuser')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error("Error checking superuser status:", error);
        throw error;
      }

      if (!profile?.is_superuser) {
        navigate("/dashboard");
        toast.error("Unauthorized access");
      }
    } catch (error) {
      console.error("Error checking superuser status:", error);
      navigate("/dashboard");
    }
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, created_at, creator_status')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setUsers(data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    }
  };

  const fetchApplications = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, application_date, application_status')
        .eq('application_status', 'pending')
        .order('application_date', { ascending: false });

      if (error) throw error;

      setApplications(data || []);
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const handleApplication = async (userId: string, approved: boolean) => {
    try {
      console.log(`Processing application for user ${userId}, approved: ${approved}`);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Authentication required");
        return;
      }

      const { data: adminProfile, error: adminError } = await supabase
        .from('profiles')
        .select('is_superuser')
        .eq('id', user.id)
        .single();

      if (adminError || !adminProfile?.is_superuser) {
        console.error("Admin verification error:", adminError);
        toast.error("Unauthorized: Superuser access required");
        return;
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          creator_status: approved ? 'creator' : 'regular',
          application_status: approved ? 'approved' : 'rejected',
          application_date: new Date().toISOString()
        })
        .eq('id', userId);

      if (updateError) {
        console.error("Error updating profile:", updateError);
        throw updateError;
      }

      const { data: updatedProfile, error: verifyError } = await supabase
        .from('profiles')
        .select('creator_status, application_status')
        .eq('id', userId)
        .single();

      if (verifyError) {
        console.error("Error verifying update:", verifyError);
        throw verifyError;
      }

      console.log("Updated profile:", updatedProfile);

      if (updatedProfile.creator_status === (approved ? 'creator' : 'regular') &&
          updatedProfile.application_status === (approved ? 'approved' : 'rejected')) {
        toast.success(`Application ${approved ? 'approved' : 'rejected'} successfully`);
        await fetchApplications(); // Refresh the applications list
        await fetchUsers(); // Refresh the users list
      } else {
        throw new Error("Status update failed to apply");
      }
    } catch (error) {
      console.error("Error handling application:", error);
      toast.error("Failed to process application. Please try again.");
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      const { data: targetUser } = await supabase
        .from('profiles')
        .select('is_superuser, username')
        .eq('id', userId)
        .single();

      if (targetUser?.is_superuser) {
        toast.error("Cannot delete superuser accounts");
        return;
      }

      console.log("Delete user functionality is ready for:", userId);
      toast.info("Delete functionality is ready but not active for safety");
      
      await fetchUsers();
    } catch (error) {
      console.error("Error in delete user function:", error);
      toast.error("An error occurred");
    }
  };

  if (loading) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full">
          <DashboardSidebar />
          <div className="flex-1 flex flex-col">
            <Header />
            <main className="flex-1 container max-w-7xl mx-auto px-4 py-8">
              <div className="animate-pulse">
                <div className="h-8 w-48 bg-muted rounded mb-8"></div>
                <Card className="p-6">
                  <div className="h-6 w-36 bg-muted rounded mb-4"></div>
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-20 bg-muted rounded"></div>
                    ))}
                  </div>
                </Card>
              </div>
            </main>
            <Footer />
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <DashboardSidebar />
        <div className="flex-1 flex flex-col">
          <Header />
          <main className="flex-1 container max-w-7xl mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-8">Admin Dashboard</h1>

            <Tabs defaultValue="applications" className="space-y-6">
              <TabsList>
                <TabsTrigger value="applications">Creator Applications</TabsTrigger>
                <TabsTrigger value="users">Manage Users</TabsTrigger>
                <TabsTrigger value="earnings">Creator Earnings</TabsTrigger>
              </TabsList>

              <TabsContent value="applications">
                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Creator Applications</h2>
                  {applications.length === 0 ? (
                    <p className="text-muted-foreground">No pending applications</p>
                  ) : (
                    <div className="space-y-4">
                      {applications.map((app) => (
                        <div
                          key={app.id}
                          className="flex items-center justify-between p-4 bg-muted rounded-lg"
                        >
                          <div>
                            <p className="font-medium">{app.username}</p>
                            <p className="text-sm text-muted-foreground">
                              Applied: {new Date(app.application_date).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleApplication(app.id, true)}
                              variant="outline"
                              size="sm"
                              className="text-green-600 hover:text-green-700"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              onClick={() => handleApplication(app.id, false)}
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="users">
                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">All Users</h2>
                  <div className="space-y-4">
                    {users.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-4 bg-muted rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{user.username}</p>
                          <p className="text-sm text-muted-foreground">
                            Joined: {new Date(user.created_at).toLocaleDateString()}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Status: {user.creator_status}
                          </p>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete User
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete User</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this user? This action cannot be undone.
                                All user data, including recipes and reviews, will be permanently deleted.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteUser(user.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                <UserX className="w-4 h-4 mr-1" />
                                Delete User
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    ))}
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="earnings">
                <CreatorEarnings />
              </TabsContent>
            </Tabs>
          </main>
          <Footer />
        </div>
      </div>
    </SidebarProvider>
  );
};

export default AdminDashboard;
